import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import FormAccordionBase from '@/components/forms/new/FormAccordionBase';
import { CompanyFormProvider } from './context/CompanyFormContext';
import { useCompanyForm } from './hooks/useCompanyForm.js';
import walkthroughIds from '../walkthroughIds.js';
import { LoadingIndicator } from '@/components/v4';

import { useNavigate } from 'react-router-dom';
import CompanyGeneral from './tabs/CompanyGeneral';
import CompanyBillingInvoice from './tabs/CompanyBillingInvoice';
import CompanyCustomAttributes from './tabs/CompanyCustomAttributes';
import CompanyAdmin from './tabs/CompanyAdmin';
import useCreateCompany from './queries/useCreateCompany';
import useGetCompanyByID from './queries/useGetCompanyByID';
import useUpdateCompany from './queries/useUpdateCompany';
import useGetEngineLocations from './queries/useGetEngineLocations';
import useGetPartnerPlans from './queries/useGetPartnerPlans';
import useGetCompanyDefinedAttributes from './queries/useGetCompanyDefinedAttributes';
import {
    getInitialFormData,
    getEngineLocationsErrorMessage,
    withAllEngineLocationsIfEmpty,
} from './utils/companyFormUtils.js';
import { redirectToNewCompany as redirectToNewCompanyAction } from './utils/redirectToNewCompany.js';
import { FORM_FIELD_PATHS } from './constants.js';
import { ServicesProvider } from 'pages/accounts/users/services/ServicesContext';
import Services from 'pages/accounts/users/services/Services';

const CompanyEditContent = ({
    initialData,
    mode,
    organisationMode,
    resetToMainTable,
    selectedCompanyID,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const { general } = walkthroughIds.companies;

    const { currentPartner, companiesAndPartners } = useSelector(
        (state) => state.navigationLists,
    );
    const isCompany = organisationMode === 'Company';
    const selectedCompany = companiesAndPartners.find(
        (i) => i.id === selectedCompanyID,
    );

    const {
        formData,
        setFormData,
        updateFormData,
        setMode,
        initialFormData,
        setInitialFormData,
        deletedBillingNoteIDs,
        residencyConfirmed,
        setEngineLocationsError,
    } = useCompanyForm();

    const [isTrial, setIsTrial] = useState(false);

    const isAddMode = mode === 'Add';
    const isEditMode = mode === 'Edit';

    useEffect(() => {
        setMode(organisationMode);
    }, [organisationMode, setMode]);
    useEffect(() => {
        if (isAddMode) {
            setFormData(getInitialFormData(initialData));
            setInitialFormData(getInitialFormData(initialData));
        }
    }, [
        isAddMode,
        currentPartner,
        setFormData,
        initialData,
        setInitialFormData,
    ]);

    const { isLoading, isFetching } = useGetCompanyByID(
        selectedCompanyID,
        (data) => {
            setFormData(data);
            setInitialFormData(data);
        },
        resetToMainTable,
        isEditMode,
    );

    const getInitialValues = () => {
        return formData;
    };

    const hasFormErrors = (errors) => {
        if (!errors || typeof errors !== 'object') return false;

        const checkNestedErrors = (obj) => {
            return Object.values(obj).some((value) => {
                if (typeof value === 'object' && value !== null) {
                    return checkNestedErrors(value);
                }
                return Boolean(value);
            });
        };

        return checkNestedErrors(errors);
    };

    const hasFormChanged = (currentValues) => {
        if (!initialFormData || !currentValues?.formValues) return true;
        return !isEqual(initialFormData, currentValues?.formValues);
    };

    const calculateIsSubmitDisabled = (errors, values) => {
        const hasErrors = hasFormErrors(errors);
        const hasChanged = hasFormChanged(values);

        const isResidencyLocked =
            !!initialFormData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID];
        const hasUnconfirmedResidencyChange =
            !isResidencyLocked &&
            !!formData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID] &&
            !residencyConfirmed;

        return hasErrors || !hasChanged || hasUnconfirmedResidencyChange;
    };

    const breadCrumbTrail = [
        { label: 'Companies', path: resetToMainTable },
        {
            label: isAddMode ? 'Add Company' : 'Edit Company',
            path: '',
        },
    ];

    const header = isAddMode
        ? 'Add Company'
        : `Edit ${selectedCompany?.name || 'Company'}`;

    const redirectToNewCompany = (result) =>
        redirectToNewCompanyAction(dispatch, navigate, result);

    const { data: availableEngineLocations } = useGetEngineLocations();
    const payloadData = withAllEngineLocationsIfEmpty(
        formData,
        availableEngineLocations,
    );

    const { mutateAsync: createCompany } = useCreateCompany(
        payloadData,
        isAddMode,
        resetToMainTable,
        redirectToNewCompany,
    );
    const { mutateAsync: updateCompany } = useUpdateCompany(
        payloadData,
        resetToMainTable,
        deletedBillingNoteIDs,
        organisationMode,
        initialFormData,
    );
    const { data: servicesCompany } = useGetCompanyDefinedAttributes(formData);

    const [defaultPlans, setDefaultPlans] = useState([]);

    // Ensure setDefaultPlans is not called with undefined/null and always sets an array
    const { isLoading: isDefaultPlansLoading } = useGetPartnerPlans((plans) => {
        setDefaultPlans(Array.isArray(plans) ? plans : []);
    });

    const defaultPlansData = { defaultPlans, isDefaultPlansLoading };

    const buildTabs = (errors, touched, values) => {
        const tabs = [
            {
                label: 'General',
                accordions: (
                    <CompanyGeneral
                        key={`${organisationMode}-general`}
                        darkMode={darkMode}
                        errors={errors}
                        touched={touched}
                        values={values}
                        isAddMode={isAddMode}
                        defaultPlansData={defaultPlansData}
                    />
                ),
            },
        ];

        // Add additional tabs for edit mode
        if (!isAddMode) {
            tabs.push(
                {
                    label: 'Billing & Invoice',
                    accordions: (
                        <CompanyBillingInvoice
                            key={`${organisationMode}-billing`}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            isCompany={isCompany}
                            organisationMode={organisationMode}
                            defaultPlansData={defaultPlansData}
                        />
                    ),
                },
                {
                    label: 'Custom Fields',
                    accordions: [
                        <CompanyCustomAttributes
                            key={`${organisationMode}-customAttributes`}
                            darkMode={darkMode}
                            errors={errors}
                        />,
                    ],
                },
                ...(servicesCompany && servicesCompany.length > 0
                    ? [
                          {
                              label: 'Services',
                              accordions: [
                                  <ServicesProvider
                                      key={`${organisationMode}-services`}>
                                      <Services
                                          companyID={formData?.id}
                                          definedAttributes={servicesCompany}
                                      />
                                  </ServicesProvider>,
                              ],
                          },
                      ]
                    : []),
                {
                    label: 'Admin',
                    accordions: [
                        <CompanyAdmin
                            key={`${organisationMode}-admin`}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            resetToMainTable={resetToMainTable}
                            isCompany={isCompany}
                        />,
                    ],
                },
            );
        }

        return tabs;
    };

    if (((isLoading || isFetching) && isEditMode) || isDefaultPlansLoading) {
        return <LoadingIndicator />;
    }

    const handleCreateAsTrial = () => {
        updateFormData('isTrial', true);
        setIsTrial(true);
    };

    const finalFormSubmit = async () => {
        const submitData = {
            ...formData,
            isTrial: isTrial ? true : formData.isTrial,
        };

        setFormData(submitData);
        setEngineLocationsError(null);
        try {
            if (isAddMode) {
                await createCompany();
            } else {
                await updateCompany();
            }
        } catch (error) {
            // Surfacing a toast/alert is already handled by the mutation's
            // own onError; just detect this specific validation failure so
            // the Data Processing Region field can be highlighted.
            const engineLocationsMessage =
                getEngineLocationsErrorMessage(error);
            if (engineLocationsMessage) {
                setEngineLocationsError(engineLocationsMessage);
            }
        }
    };

    return (
        <>
            <FormAccordionBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={getInitialValues()}
                finalFormSubmit={finalFormSubmit}
                backLink={resetToMainTable}
                submitButtonText={isAddMode ? 'Create' : 'Save'}
                middleButton={
                    isAddMode
                        ? {
                              text: 'Create as Trial',
                              onClick: handleCreateAsTrial,
                          }
                        : null
                }
                isSubmitDisabled={calculateIsSubmitDisabled || true}
                ufBackBtn={general.ufBackBtn}
                ufSaveBtn={general.ufSaveBtn}
                enableReinitialize={true}
                enableTabs={true}
                defaultTabIndex={0}>
                {({ errors, touched, values }) =>
                    buildTabs(errors, touched, values)
                }
            </FormAccordionBase>
        </>
    );
};

const CompanyEdit = ({
    mode,
    organisationMode,
    resetToMainTable,
    selectedCompanyID,
}) => {
    const { currentPartner, currentWholesaler } = useSelector(
        (state) => state.navigationLists,
    );
    const initialData = {
        partnerID: currentPartner,
        wholesalerID: currentWholesaler,
    };

    return (
        <CompanyFormProvider initialData={initialData}>
            <CompanyEditContent
                mode={mode}
                organisationMode={organisationMode}
                resetToMainTable={resetToMainTable}
                selectedCompanyID={selectedCompanyID}
                initialData={initialData}
            />
        </CompanyFormProvider>
    );
};

export default CompanyEdit;
